/**
 * What: Shared definitions for the "running a study" decorator operations -- start (dry run or
 *   paid StudyRun), packet status, results, and setup-preparation status (plan
 *   https://github.com/Yesterday-Work/ssr-validation-tool/blob/fdcf45fb4/docs/plans/2026-09-28-shopify-admin-api-parity.md, issue #3682).
 * Transport / data-flow: Pure zod contracts + `ShopifyOperationDefinition` metadata, imported by
 *   the Shopify Worker (handlers + merchant/operator dispatch) and Web (generated Admin actions).
 *   Path parameters (`packetId`, `id`) are part of each request schema so the operator path, which
 *   has no URL, carries them in `input`. Only the operator dispatch route runs `requestSchema`; the merchant
 *   route (registry) checks the response only, so its body handling is unchanged. `/api/submit-product-study` stays undecorated.
 */
import * as z from 'zod';
import { shopifyCreditBalanceSchema } from './shopify-credit-activity-v1.js';
import { shopifySetupPacketStatusResponseSchema } from './shopify-setup-packet-list.js';
import { shopifyStudyResultsResponseSchema } from './shopify-study-results-v1.js';
import { automaticResponsePhraseBudgetSchema } from './study-automatic-enrichment.js';
export const shopifyStudyRunStartRequestSchema = z.object({
    packetId: z.string(),
    dryRun: z.boolean().optional(),
    acknowledgedPreflightHash: z.string().optional(),
    runRequestId: z.string().optional(),
    targetScope: z.enum(['study', 'arm', 'failed-arms']).optional(),
    targetArmId: z.string().nullish(),
    retrySourceRunId: z.string().nullish(),
});
/** Union of the run receipts (dry-run, submitted, blocked, failed, in-progress): the keys the UI
 *  reads are typed, anything else the owner returns passes through untouched. */
export const shopifyStudyRunStartResponseSchema = z.looseObject({
    success: z.boolean(),
    dry_run: z.boolean().optional(),
    status: z.string().optional(),
    studyId: z.string().optional(),
    studyRunId: z.string().optional(),
    runRequestId: z.string().optional(),
    submission_outcome: z.string().optional(),
    code: z.string().optional(),
    error: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
    preflightHash: z.string().optional(),
    execution: z.record(z.string(), z.unknown()).optional(),
    methodology: z.record(z.string(), z.unknown()).optional(),
    // Mirrors the Web `PreflightStudyRunResult` (apps/web/src/lib/study-run-submission-preflight.ts:54);
    // the Shopify app reads only `model` (StudyRunPage.tsx:139), so the rest passes through.
    preflight: z
        .looseObject({ model: z.string(), automaticEnrichment: automaticResponsePhraseBudgetSchema.optional() })
        .optional(),
    creditBalance: shopifyCreditBalanceSchema.optional(),
    quotedCredits: z.number().optional(),
    availableAfterNextRun: z.number().optional(),
    canRun: z.boolean().optional(),
});
export const SHOPIFY_STUDY_RUN_START_OPERATION = {
    id: 'study-run.start',
    method: 'POST',
    path: '/api/setups/:packetId/run',
    requestSchema: shopifyStudyRunStartRequestSchema,
    responseSchema: shopifyStudyRunStartResponseSchema,
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    effects: [],
    // This authenticated legacy operation always refuses; direct study-run owns execution.
    risk: { cost: 'free', destructive: false, dryRunCapable: true },
    idempotent: true,
};
export const shopifyStudyRunStatusRequestSchema = z.object({ packetId: z.string() });
export const SHOPIFY_STUDY_RUN_STATUS_OPERATION = {
    id: 'study-run.status',
    method: 'GET',
    path: '/api/setups/:packetId/status',
    requestSchema: shopifyStudyRunStatusRequestSchema,
    responseSchema: shopifySetupPacketStatusResponseSchema,
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    effects: [],
    risk: { cost: 'free', destructive: false, dryRunCapable: false },
    idempotent: true,
};
export const shopifyStudyRunResultsRequestSchema = z.object({
    packetId: z.string(),
    runId: z.string().nullish(),
});
export const SHOPIFY_STUDY_RUN_RESULTS_OPERATION = {
    id: 'study-run.results',
    method: 'GET',
    path: '/api/setups/:packetId/results',
    requestSchema: shopifyStudyRunResultsRequestSchema,
    responseSchema: shopifyStudyResultsResponseSchema,
    // Authenticated retirement only: no provider token, product read or stored binding lookup.
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    effects: [],
    risk: { cost: 'free', destructive: false, dryRunCapable: false },
    idempotent: true,
};
export const shopifyStudyRunPrepareStatusRequestSchema = z.object({ id: z.string() });
export const shopifyStudyRunPrepareStatusResponseSchema = z.object({
    status: z.string(),
    report: z.record(z.string(), z.unknown()).optional(),
});
export const SHOPIFY_STUDY_RUN_PREPARE_STATUS_OPERATION = {
    id: 'study-run.prepare-status',
    method: 'GET',
    path: '/api/run-status/:id',
    requestSchema: shopifyStudyRunPrepareStatusRequestSchema,
    responseSchema: shopifyStudyRunPrepareStatusResponseSchema,
    auth: { merchant: true, operator: true, requiresOfflineToken: false, merchantOnlyGates: [] },
    // Historical preparation polling is retired; it cannot dispatch work.
    effects: [],
    risk: { cost: 'free', destructive: false, dryRunCapable: false },
    idempotent: true,
};
//# sourceMappingURL=shopify-study-run-operation.js.map